<template>
  <div class="database-page">
    <div class="page-top-bar">
      <h2 class="page-title">基础数据库</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">关键字</span>
        <el-input
          v-model="keyword"
          size="small"
          placeholder="因子名称"
          clearable
          style="width: 160px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">排放类别</span>
        <el-select v-model="category" size="small" clearable placeholder="全部" style="width: 140px">
          <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">数据来源</span>
        <el-select v-model="source" size="small" clearable placeholder="全部" style="width: 160px">
          <el-option v-for="item in sources" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="因子总数"
        :today="summary.totalFactors.today"
        :annual="summary.totalFactors.annual"
        :unit="summary.totalFactors.unit"
        :annual-unit="summary.totalFactors.unit"
        icon="el-icon-coin"
        color="#409EFF"
      />
      <stat-card
        title="本年度更新"
        :today="summary.updatedThisYear.today"
        :annual="summary.updatedThisYear.annual"
        :unit="summary.updatedThisYear.unit"
        :annual-unit="summary.updatedThisYear.unit"
        icon="el-icon-refresh"
        color="#E6A23C"
      />
      <stat-card
        title="引用次数"
        :today="summary.totalRefs.today"
        :annual="summary.totalRefs.annual"
        :unit="summary.totalRefs.unit"
        :annual-unit="summary.totalRefs.unit"
        icon="el-icon-link"
        color="#67C23A"
      />
    </div>

    <div class="table-section">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="排放因子库" name="factor">
          <div class="table-toolbar">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openFactorDialog()">
              新增因子
            </el-button>
          </div>
          <el-table :data="emissionFactors" border stripe size="small">
            <el-table-column prop="name" label="因子名称" min-width="180" />
            <el-table-column prop="category" label="排放类别" width="140" />
            <el-table-column prop="unit" label="单位" width="120" />
            <el-table-column prop="value" label="因子值" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.value, 4) }}</template>
            </el-table-column>
            <el-table-column prop="source" label="数据来源" min-width="160" show-overflow-tooltip />
            <el-table-column prop="year" label="适用年份" width="100" align="center" />
            <el-table-column prop="refCount" label="引用次数" width="100" align="right" />
            <el-table-column label="操作" width="100" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="openFactorDialog(row)">编辑</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="GWP 值" name="gwp">
          <el-table :data="gwpValues" border stripe size="small">
            <el-table-column prop="gas" label="温室气体种类" width="140" />
            <el-table-column prop="gwp" label="GWP 值" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.gwp, 0) }}</template>
            </el-table-column>
            <el-table-column prop="version" label="版本" width="100" align="center" />
            <el-table-column prop="standard" label="参考标准" min-width="160" />
            <el-table-column prop="year" label="适用年份" width="100" align="center" />
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="热值与换算系数" name="heating">
          <el-table :data="heatingValues" border stripe size="small">
            <el-table-column prop="energyType" label="能源品种" width="140" />
            <el-table-column prop="lowerHeatingValue" label="低位热值" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.lowerHeatingValue, 3) }}</template>
            </el-table-column>
            <el-table-column prop="unit" label="单位" width="120" />
            <el-table-column prop="coefficient" label="换算系数" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.coefficient, 4) }}</template>
            </el-table-column>
            <el-table-column prop="year" label="适用年份" width="100" align="center" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog
      :title="factorDialogTitle"
      :visible.sync="factorDialogVisible"
      width="520px"
      @close="resetFactorForm"
    >
      <el-form :model="factorForm" label-width="100px" size="small">
        <el-form-item label="因子名称" required>
          <el-input v-model="factorForm.name" placeholder="请输入因子名称" />
        </el-form-item>
        <el-form-item label="排放类别" required>
          <el-select v-model="factorForm.category" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="单位" required>
          <el-input v-model="factorForm.unit" placeholder="如 tCO2/万m³" />
        </el-form-item>
        <el-form-item label="因子值" required>
          <el-input-number v-model="factorForm.value" :precision="4" :step="0.0001" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="数据来源">
          <el-input v-model="factorForm.source" placeholder="如 GB/T 32150 附录B" />
        </el-form-item>
        <el-form-item label="适用年份">
          <el-date-picker
            v-model="factorForm.year"
            type="year"
            size="small"
            placeholder="选择年份"
            value-format="yyyy"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="factorDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveFactor">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getDatabaseData } from '@/api/modules/carbonAccounting'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAccountingDatabase',
  components: { FilterBar, StatCard },
  data() {
    return {
      keyword: '',
      category: '',
      source: '',
      categories: [],
      sources: [],
      summary: {
        totalFactors: { today: 0, annual: 0, unit: '条' },
        updatedThisYear: { today: 0, annual: 0, unit: '条' },
        totalRefs: { today: 0, annual: 0, unit: '次' }
      },
      activeTab: 'factor',
      emissionFactors: [],
      gwpValues: [],
      heatingValues: [],
      factorDialogVisible: false,
      factorDialogTitle: '新增因子',
      factorForm: {
        id: null,
        name: '',
        category: '',
        unit: '',
        value: 0,
        source: '',
        year: '2025',
        refCount: 0
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getDatabaseData({
        keyword: this.keyword,
        category: this.category,
        source: this.source
      })
      const data = res.data
      this.categories = data.categories
      this.sources = data.sources
      this.summary = data.summary
      this.emissionFactors = data.emissionFactors
      this.gwpValues = data.gwpValues
      this.heatingValues = data.heatingValues
    },
    openFactorDialog(row) {
      if (row) {
        this.factorDialogTitle = '编辑因子'
        this.factorForm = { ...row, year: String(row.year) }
      } else {
        this.factorDialogTitle = '新增因子'
        this.resetFactorForm()
      }
      this.factorDialogVisible = true
    },
    resetFactorForm() {
      this.factorForm = {
        id: null,
        name: '',
        category: '',
        unit: '',
        value: 0,
        source: '',
        year: '2025',
        refCount: 0
      }
    },
    saveFactor() {
      const year = Number(this.factorForm.year) || 2025
      if (this.factorForm.id) {
        const idx = this.emissionFactors.findIndex(f => f.id === this.factorForm.id)
        if (idx >= 0) {
          this.$set(this.emissionFactors, idx, { ...this.factorForm, year })
        }
      } else {
        this.emissionFactors.push({
          ...this.factorForm,
          id: Date.now(),
          year,
          refCount: 0
        })
      }
      this.factorDialogVisible = false
      this.$message.success('保存成功')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-top-bar {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}
</style>
